/**
 * AppContext
 *
 * Global application context for managing:
 * - Active menu item selection
 * - Menu items data
 * - Current page content
 * - Content loading state
 */

import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

import type { MenuItem, PageContent, SiteSettings } from '../services/api';
import api from '../services/api';

/**
 * AppContext State Interface
 */
export type AppContextState = {
  // Active menu state
  readonly activeMenuItem: string;
  readonly setActiveMenuItem: (slug: string) => void;

  // Active category filter (for submenus)
  readonly activeCategory: string | null;
  readonly setActiveCategory: (categorySlug: string | null) => void;

  // Menu items
  readonly menuItems: ReadonlyArray<MenuItem>;
  readonly setMenuItems: (items: ReadonlyArray<MenuItem>) => void;
  readonly loadMenuItems: () => Promise<void>;

  // Current page content
  readonly content: PageContent | null;
  readonly loadContent: (slug: string) => Promise<void>;

  // Loading states
  readonly isLoadingMenu: boolean;
  readonly isLoadingContent: boolean;

  // Errors
  readonly menuError: string | null;
  readonly contentError: string | null;

  // Site settings
  readonly siteSettings: SiteSettings | null;
};

/**
 * Create the context with undefined default (will be provided by AppProvider)
 */
const AppContext = createContext<AppContextState | undefined>(undefined);

/**
 * AppProvider Props
 */
type AppProviderProps = {
  readonly children: ReactNode;
};

/**
 * AppProvider Component
 *
 * Wraps the application and provides global state management
 * @param root0
 * @param root0.children
 */
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // Active menu item state
  const [activeMenuItem, setActiveMenuItem] = useState<string>('about-me'); // Default to first menu item

  // Active category filter state (null = show all)
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Menu items state
  const [menuItems, setMenuItems] = useState<ReadonlyArray<MenuItem>>([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState<boolean>(false);
  const [menuError, setMenuError] = useState<string | null>(null);

  // Content state
  const [content, setContent] = useState<PageContent | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);
  const [contentError, setContentError] = useState<string | null>(null);

  // Site settings state
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);

  /**
   * Load menu items from API
   */
  const loadMenuItems = useCallback(async () => {
    setIsLoadingMenu(true);
    setMenuError(null);

    try {
      const items = await api.menu.getAll();

      // Filter only enabled items and sort by order
      const enabledItems = items.filter((item) => item.enabled).sort((a, b) => a.order - b.order);

      setMenuItems(enabledItems);

      // Set first item as active if no active item is set
      // Use callback form to avoid dependency on activeMenuItem
      setActiveMenuItem((currentActive) => {
        if (!currentActive && enabledItems.length > 0) {
          return enabledItems[0].slug;
        }
        return currentActive;
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load menu items';
      setMenuError(errorMessage);
      console.error('Error loading menu items:', error);
    } finally {
      setIsLoadingMenu(false);
    }
  }, []); // No dependencies needed now

  /**
   * Load page content by slug
   */
  const loadContent = useCallback(async (slug: string) => {
    setIsLoadingContent(true);
    setContentError(null);

    try {
      const pageContent = await api.content.getBySlug(slug);
      setContent(pageContent);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : `Failed to load content for ${slug}`;
      setContentError(errorMessage);
      console.error(`Error loading content for ${slug}:`, error);
    } finally {
      setIsLoadingContent(false);
    }
  }, []);

  /**
   * Reset active category when menu item changes
   */
  useEffect(() => {
    setActiveCategory(null);
  }, [activeMenuItem]);

  /**
   * Load site settings on mount and update document title
   */
  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        const settings = await api.siteSettings.get();
        setSiteSettings(settings);
        if (settings.siteTitle) {
          document.title = settings.siteTitle;
        }
      } catch (error) {
        console.error('Failed to load site settings:', error);
      }
    };

    loadSiteSettings();
  }, []);

  /**
   * Context value
   */
  const value: AppContextState = {
    // Active menu
    activeMenuItem,
    setActiveMenuItem,

    // Active category filter
    activeCategory,
    setActiveCategory,

    // Menu items
    menuItems,
    setMenuItems,
    loadMenuItems,

    // Content
    content,
    loadContent,

    // Loading states
    isLoadingMenu,
    isLoadingContent,

    // Errors
    menuError,
    contentError,

    // Site settings
    siteSettings,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

/**
 * useApp Hook
 *
 * Custom hook to access AppContext
 * Throws error if used outside of AppProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useApp = (): AppContextState => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }

  return context;
};

export default AppContext;
